import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'super_admin_token';

const SuperAdminAuthContext = createContext(null);

export const useSuperAdminAuth = () => {
  const ctx = useContext(SuperAdminAuthContext);
  if (!ctx) throw new Error('useSuperAdminAuth must be used within provider');
  return ctx;
};

export const SuperAdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const loadMe = useCallback(async (tok) => {
    const t = tok || token;
    if (!t) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await axios.get(`${API}/super/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
      return res.data;
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    // Uses the SAME /auth/login as tenants. Super-admin check happens on
    // /super/me — a non-super user would get 403 and we refuse to proceed.
    const form = new URLSearchParams();
    form.append('username', email);
    form.append('password', password);
    // Same post-deploy 503 absorption as the tenant login (see
    // /app/frontend/src/lib/api.js authAPI.login). Without this,
    // super-admins also hit "Invalid credentials" right after a
    // redeploy while Mongo's connection pool warms up.
    const delays = [0, 1500, 3500];
    let res;
    let lastErr;
    for (let i = 0; i < delays.length; i++) {
      if (delays[i]) await new Promise((r) => setTimeout(r, delays[i]));
      try {
        res = await axios.post(`${API}/auth/login`, form, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        break;
      } catch (err) {
        lastErr = err;
        if (err.response?.status !== 503) throw err;
      }
    }
    if (!res) throw lastErr;
    const t = res.data.access_token;
    // Verify super-admin status before saving the token
    try {
      const me = await axios.get(`${API}/super/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setUser(me.data);
      return { ok: true, user: me.data };
    } catch (err) {
      if (err.response?.status === 403) {
        return { ok: false, error: 'This account does not have super-admin access.' };
      }
      return { ok: false, error: err.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const hasPerm = (perm) => {
    if (!user?.permissions) return false;
    return user.permissions.includes(perm);
  };

  return (
    <SuperAdminAuthContext.Provider value={{ user, token, loading, login, logout, hasPerm, reload: loadMe }}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};

export const superApi = axios.create({ baseURL: API });

// Inject the super-admin token automatically for calls under this layout
superApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
